// Global Instructions Rule Applied!
//
// Investor form — an investor ENTITY (public.investors) as a record in its
// own app tab (funds / investors amendments PR 4, 2026-10-02). It replaces
// the "Edit Investor" pop-up of the fund Investors tab and is where an
// investor is created under an account (/investors/forms/full/new?account=).
//
// Sections: Investor (name, legal name, investor number, account),
// Classification (classification, legal entity type, SBIC investor type),
// Capital call notices (the additional notice documents every commitment of
// the investor carries unless it has its own setting) and, once the investor
// exists, its Commitments: each opens its own form, and "+ Commitment" adds
// one for this investor.
//
// The form holds a draft and saves explicitly: [Cancel | Close] [Create →
// Save], Save and Cancel only while there are unsaved changes. There is no
// Delete: nothing can trash an investor.

import React from 'react'
import { Button, Col, Row, Spin, Typography } from 'antd'

import { FormSection } from '../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { useRefreshOnActivate } from '../../../../core/hooks/useRefreshOnActivate.ts'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_COMMITMENTS, TABLE_CONSOLIDATED_COMMITMENTS } from '../../../../state_manager/globals.ts'
import { useInvestorForm } from '../../hooks/useInvestorForm.ts'
import { InvestorCommitmentsSection } from '../InvestorCommitmentsSection.tsx'

const { Title } = Typography

interface FullInvestorFormProps {
  recordId?: string | null
  isNew?: boolean
}

export default function FullInvestorForm({ recordId = null, isNew = false }: FullInvestorFormProps): React.ReactElement {
  const form = useInvestorForm(recordId ?? null, isNew || !recordId)
  const creating = isNew || !recordId
  const { openFormInTab } = useTabManagerContext()
  // a commitment added or changed on its own form shows here when the user
  // comes back to this tab
  const attachRefresh = useRefreshOnActivate(form.loadCommitments, { maxAgeMs: 0 })

  if (form.loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }

  if (!creating && !form.detail) {
    return (
      <div className='p-10 text-center text-gray-500'>
        {form.notFound ? 'Investor not found.' : form.loadError || 'The investor could not be loaded.'}
        <div className='mt-3'>
          <Button onClick={form.handleClose}>Close</Button>
        </div>
      </div>
    )
  }

  const inputProps = {
    inputMode: 'edit' as const,
    labelPosition: 'above' as const,
    onChange: form.handleChange,
    validationErrors: form.validationErrors,
    validationErrorsVersion: form.validationErrorsVersion
  }

  return (
    <div ref={attachRefresh} className='h-full overflow-y-auto p-4'>
      <div className='flex items-center justify-between mb-3'>
        <Title level={4} className='!mb-0'>
          {creating ? 'New Investor' : form.detail?.investors__name}
        </Title>
        <div className='flex items-center gap-2'>
          {/* Fixed grammar: [dismiss Cancel | Close] [primary Create → Save];
              Save and Cancel only while there are unsaved changes. */}
          {form.dirty ? (
            <Button disabled={form.saving} onClick={form.handleCancel}>
              Cancel
            </Button>
          ) : creating ? null : (
            <Button onClick={form.handleClose}>Close</Button>
          )}
          {creating ? (
            <Button type='primary' loading={form.saving} onClick={() => void form.handleSave()}>
              Create
            </Button>
          ) : form.dirty ? (
            <Button type='primary' loading={form.saving} onClick={() => void form.handleSave()}>
              Save
            </Button>
          ) : null}
        </div>
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={24} xl={12}>
          <FormSection title='Investor' titleColor='blue' scrollable={false}>
            <InputManager key={`investorFields-${form.resetVersion}`} {...inputProps} fields={form.investorFields} />
            {/* the account of an existing investor cannot be changed: a
                record link, drawn by a read-mode InputManager */}
            {form.accountLinkFields.length > 0 ? (
              <InputManager inputMode='read' labelPosition='above' fields={form.accountLinkFields} />
            ) : null}
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Classification' titleColor='blue' scrollable={false}>
            <InputManager key={`classificationFields-${form.resetVersion}`} {...inputProps} fields={form.classificationFields} />
          </FormSection>
        </Col>
        <Col xs={24}>
          <FormSection title='Capital call notices' titleColor='purple' scrollable={false}>
            <InputManager key={`noticeFields-${form.resetVersion}`} {...inputProps} fields={form.noticeFields} />
          </FormSection>
        </Col>
        {creating ? null : (
          <Col xs={24}>
            <InvestorCommitmentsSection
              commitments={form.commitments}
              investorName={form.detail?.investors__name}
              loading={form.commitmentsLoading}
              onOpenCommitment={(row) =>
                openFormInTab(
                  row.prog__kind === 'consolidated' ? TABLE_CONSOLIDATED_COMMITMENTS : TABLE_COMMITMENTS,
                  row.prog__commitment_id,
                  `${form.detail?.investors__name ?? ''} — ${row.fund_groups__name ?? row.funds__name ?? ''}`
                )
              }
              onAddCommitment={() =>
                openFormInTab(TABLE_COMMITMENTS, null, 'New Commitment', {
                  investor: form.detail?.investors__id,
                  account: form.detail?.accounts__id
                })
              }
            />
          </Col>
        )}
      </Row>
    </div>
  )
}
